import { useState } from "react";
import { UserPlus } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { usePermissao } from "@/lib/use-permissao";
import { cn } from "@/lib/cn";
import { resolveDashboardMode } from "@/lib/dashboard-mode";
import { TeamMembersList } from "@/components/team/TeamMembersList";
import { TeamInviteDialog } from "@/components/team/TeamInviteDialog";
import { TeamVisaoGeral } from "@/components/team/TeamVisaoGeral";
import { SemPermissao } from "./SemPermissao";

type Aba = "visao" | "membros";

/** Equipe da organização em foco — as mesmas duas leituras de /equipe: "Visão geral" (a operação
 *  do dia, aberta por padrão) e "Membros e acessos" (cartões, tipo de acesso e permissões). */
export function PainelEquipe() {
  const { user } = useAuth();
  const podeGerenciar = usePermissao("team.manage");
  const [aba, setAba] = useState<Aba>("visao");
  const [convidando, setConvidando] = useState(false);

  if (!podeGerenciar) return <SemPermissao area="a equipe" />;
  const mode = resolveDashboardMode(user?.activeOrganization?.types ?? []);
  const podeConvidar = !user?.activeOrganization?.isPersonal && !!user?.activeOrganization?.canManageTeam;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-100 dark:border-border">
        <div className="flex gap-1" role="tablist" aria-label="Seções da equipe">
          {(
            [
              { chave: "visao", rotulo: "Visão geral" },
              { chave: "membros", rotulo: "Membros e acessos" },
            ] as const
          ).map((t) => (
            <button
              key={t.chave}
              type="button"
              role="tab"
              aria-selected={aba === t.chave}
              onClick={() => setAba(t.chave)}
              className={cn(
                "-mb-px border-b-2 px-3 py-2.5 text-[13px] font-medium transition-colors",
                aba === t.chave
                  ? "border-brand-600 font-semibold text-brand-700 dark:border-primary dark:text-primary"
                  : "border-transparent text-zinc-500 hover:text-zinc-800 dark:text-muted-foreground dark:hover:text-foreground",
              )}
            >
              {t.rotulo}
            </button>
          ))}
        </div>
        {podeConvidar && (
          <button
            type="button"
            onClick={() => setConvidando(true)}
            className="mb-1.5 inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
          >
            <UserPlus className="h-4 w-4" />
            Convidar
          </button>
        )}
      </div>

      {aba === "visao" ? <TeamVisaoGeral mode={mode} /> : <TeamMembersList canManage />}
      <TeamInviteDialog open={convidando} onClose={() => setConvidando(false)} />
    </div>
  );
}
